import { BETA_FEATURES, type BetaFeatureSpec } from './betaFeatures';

export const TREND_SIZE = { width: 1000, height: 240 } as const;

export const TREND_PLOT = { left: 32, right: 992, top: 8, bottom: 208 } as const;

const MAX_LABELS = 8;

const MIN_END_LABEL_GAP = 14;

const MIN_TOP = 5;

export type TrendSeries = {
  feature: BetaFeatureSpec;
  color: string;
  values: number[];
};

export type Trend = {
  periods: string[];
  series: TrendSeries[];
};

export type EndLabel = { text: string; x: number; y: number };

export function trendSeries(periods: string[], lines: readonly (readonly [BetaFeatureSpec, number[]])[]): Trend {
  const byId = new Map(lines.map(([feature, values]) => [feature.id, { feature, color: feature.color, values }]));
  return {
    periods,
    series: BETA_FEATURES.flatMap((feature) => {
      const line = byId.get(feature.id);
      return line ? [line] : [];
    }),
  };
}

export function trendScale(max: number): { top: number; ticks: number[] } {
  let step = 1;
  for (const candidate of [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000]) {
    step = candidate;
    if (max / candidate <= 4) break;
  }
  const top = Math.max(MIN_TOP, Math.ceil(max / step) * step);
  const ticks: number[] = [];
  for (let value = 0; value <= top; value += step) ticks.push(value);
  return { top, ticks };
}

export function xOf(index: number, count: number): number {
  const { left, right } = TREND_PLOT;
  if (count <= 1) return (left + right) / 2;
  return left + ((right - left) * index) / (count - 1);
}

export function yOf(value: number, top: number): number {
  return TREND_PLOT.bottom - ((TREND_PLOT.bottom - TREND_PLOT.top) * value) / top;
}

export function linePath(values: number[], top: number): string {
  return values.map((value, index) => `${index === 0 ? 'M' : 'L'}${xOf(index, values.length)} ${yOf(value, top)}`).join(' ');
}

export function labelIndexes(count: number): number[] {
  if (count <= MAX_LABELS) return Array.from({ length: count }, (_, index) => index);
  const stride = Math.ceil(count / MAX_LABELS);
  const indexes: number[] = [];
  for (let index = count - 1; index >= 0; index -= stride) indexes.unshift(index);
  return indexes;
}

export function endLabels(series: TrendSeries[], top: number): EndLabel[] {
  const labels: EndLabel[] = [];
  for (const line of series) {
    const last = line.values.length - 1;
    const value = line.values[last];
    if (value === undefined) continue;
    if (labels.some((label) => label.text === String(value))) continue;
    labels.push({ text: String(value), x: xOf(last, line.values.length), y: yOf(value, top) });
  }
  const crowded = labels.some((a, i) => labels.some((b, j) => i < j && Math.abs(a.y - b.y) < MIN_END_LABEL_GAP));
  return crowded ? [] : labels;
}
